import React, { useState } from "react";
import { createPortal } from "react-dom";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL } from "../../config/constants";
import { MOOD_ICONS } from "../../config/moodIcons";
import { emitFormSaved } from "../../formEvents";
import {
  Overlay, Modal, Title, Period,
  Question, QuestionLabel, NotesLabel, OptionList, Option, RadioInput, RadioMark, MoodIcon,
  Textarea, ActionRow, CancelButton, SaveButton,
} from "./styles";

// Mismos valores y límite que valida el backend (PUT /me/weekly-form).
const QUESTIONS = [
  { field: "activity_frequency", options: ["multiple_daily", "once_daily", "days_5_6", "days_3_4", "days_1_2", "never"] },
  { field: "performance",        options: ["easy", "partial", "difficult"] },
  { field: "mood",               options: ["cooperative", "neutral", "uncomfortable"] },
];
const NOTES_MAX = 2000;

// 'YYYY-MM-DD' → 'DD/MM/YYYY' sin pasar por Date, para evitar desfases de zona horaria.
const fmtDate = (ymd) => ymd.split("-").reverse().join("/");

/**
 * Rellena o edita el formulario de la semana vigente. `state` es la respuesta
 * de GET /me/weekly-form; si trae `form`, el modal abre en modo edición.
 */
const WeeklyFormModal = ({ state, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!state.form;
  const [answers, setAnswers] = useState({
    activity_frequency: state.form?.activity_frequency || "",
    performance:        state.form?.performance || "",
    mood:               state.form?.mood || "",
    notes:              state.form?.notes || "",
  });
  const [loading, setLoading] = useState(false);

  const setAnswer = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));
  const complete = QUESTIONS.every(({ field }) => answers[field]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complete || loading) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/me/weekly-form`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ week_number: state.week_number, ...answers, notes: answers.notes.trim() }),
      });
      const data = await res.json();

      // La semana ha cambiado con el modal abierto: se descarta y la tarjeta pasa al estado nuevo.
      if (res.status === 409) {
        toast.error(t("weeklyForm.toastClosed"));
        emitFormSaved({ type: "weekly", state: data.state });
        onSaved(data.state);
        return;
      }
      if (!res.ok) throw new Error();

      toast.success(t(isEdit ? "weeklyForm.toastUpdated" : "weeklyForm.toastSubmitted"));
      emitFormSaved({ type: "weekly", state: data });
      onSaved(data);
    } catch {
      toast.error(t("weeklyForm.toastError"));
      setLoading(false);
    }
  };

  // Portal a <body>: desde la barra lateral (position: fixed + z-index) el
  // overlay quedaría atrapado en su contexto de apilamiento, bajo la top bar.
  return createPortal(
    <Overlay {...useBackdropClose(onClose)}>
      <Modal onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div>
          <Title>{t("weeklyForm.modalTitle")}</Title>
          <Period>
            {t("weeklyForm.period", {
              start: fmtDate(state.week_start_date),
              end: fmtDate(state.week_end_date),
            })}
          </Period>
        </div>

        {QUESTIONS.map(({ field, options }) => (
          <Question key={field}>
            <QuestionLabel>{t(`weeklyForm.questions.${field}`)}</QuestionLabel>
            <OptionList>
              {options.map((option) => (
                <Option key={option}>
                  <RadioInput
                    type="radio"
                    name={field}
                    value={option}
                    checked={answers[field] === option}
                    onChange={() => setAnswer(field, option)}
                  />
                  <RadioMark />
                  {field === "mood" && <MoodIcon src={MOOD_ICONS[option]} alt="" />}
                  {t(`weeklyForm.options.${field}.${option}`)}
                </Option>
              ))}
            </OptionList>
          </Question>
        ))}

        <div>
          <NotesLabel htmlFor="wf-notes">{t("weeklyForm.questions.notes")}</NotesLabel>
          <Textarea
            id="wf-notes"
            maxLength={NOTES_MAX}
            value={answers.notes}
            onChange={(e) => setAnswer("notes", e.target.value)}
          />
        </div>

        <ActionRow>
          <CancelButton type="button" onClick={onClose}>{t("weeklyForm.cancel")}</CancelButton>
          <SaveButton type="submit" disabled={!complete || loading}>
            {loading ? t("weeklyForm.saving") : t(isEdit ? "weeklyForm.save" : "weeklyForm.submit")}
          </SaveButton>
        </ActionRow>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default WeeklyFormModal;
